import APIService from "./APIService";

/**
 * API service for Platform Admin Scam Reports.
 * Provides access to all scam reports across the platform.
 * Requires platform_admin role.
 */
class AdminScamReportAPI {
  static BASE_PATH = "/api/v1/admin/scam-reports";

  /**
   * Get all scam reports (paginated, with optional filters).
   *
   * @param {number} [page=0]          - Page number (0-based)
   * @param {number} [size=25]         - Page size
   * @param {string} [analysisType]    - Filter by type: URL_SAFETY, SMS_ANALYSIS, etc.
   * @param {string} [riskLevel]       - Filter by risk: LOW, MEDIUM, HIGH, CRITICAL
   * @param {boolean} [scamOnly=false] - Only show confirmed scams
   * @param {number} [days]            - Only show reports from last N days
   * @param {string} [userUuid]        - Filter by user UUID (substring match)
   * @returns {Promise<Object>} { reports, totalElements, totalPages, currentPage }
   */
  static async getAllReports(
    page = 0,
    size = 25,
    { analysisType, riskLevel, scamOnly, days, userUuid } = {},
  ) {
    try {
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("size", size);
      if (analysisType) params.append("analysisType", analysisType);
      if (riskLevel) params.append("riskLevel", riskLevel);
      if (scamOnly) params.append("scamOnly", "true");
      if (days) params.append("days", days);
      if (userUuid) params.append("userUuid", userUuid);
      const response = await APIService.get(
        `${AdminScamReportAPI.BASE_PATH}?${params.toString()}`,
      );
      return response;
    } catch (error) {
      console.error("AdminScamReportAPI: Error fetching reports:", error);
      throw error;
    }
  }

  /**
   * Get platform-wide scam report statistics.
   *
   * @returns {Promise<Object>} { totalReports, scamCount, safeCount, byAnalysisType }
   */
  static async getStats() {
    try {
      const response = await APIService.get(
        `${AdminScamReportAPI.BASE_PATH}/stats`,
      );
      return response;
    } catch (error) {
      console.error("AdminScamReportAPI: Error fetching stats:", error);
      throw error;
    }
  }

  /**
   * Get a single scam report by ID.
   *
   * @param {number} reportId - Report ID
   * @returns {Promise<Object>} The full scam report
   */
  static async getReport(reportId) {
    try {
      const response = await APIService.get(
        `${AdminScamReportAPI.BASE_PATH}/${reportId}`,
      );
      return response;
    } catch (error) {
      console.error(
        "AdminScamReportAPI: Error fetching report:",
        reportId,
        error,
      );
      throw error;
    }
  }

  /**
   * Delete a scam report by ID.
   *
   * @param {number} reportId - Report ID
   * @returns {Promise<void>}
   */
  static async deleteReport(reportId) {
    try {
      await APIService.delete(`${AdminScamReportAPI.BASE_PATH}/${reportId}`);
    } catch (error) {
      console.error(
        "AdminScamReportAPI: Error deleting report:",
        reportId,
        error,
      );
      throw error;
    }
  }
}

export default AdminScamReportAPI;
